import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, timer } from 'rxjs';
import { exhaustMap, filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import {
  PublishDealModalComponent,
  PublishDealModalResult,
  PublishDealOptions,
} from '../../features/deal/components/publish-deal-modal/publish-deal-modal.component';
import { TransactionService } from '../../features/home/services/transaction.service';
import {
  createNotificationEffect,
  PushNotificationRecord,
} from '../../features/shared/utils/create-notification-effect';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { AppActions } from '../app/app.actions';
import { ManagerSelectors } from '../manager/manager.selectors';
import { RootState } from '../root-state.model';
import { TransactionActions } from './transaction.actions';
import { TransactionSelectors } from './transaction.selectors';
import { RepoDocBundleService } from '../../features/catalog/services/repo-document-bundle.service';

@Injectable()
export class TransactionEffects {
  private readonly transactionService = inject(TransactionService);
  private readonly repoDocBundleService = inject(RepoDocBundleService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly dialog = inject(MatDialog);

  private readonly pollingTransactionChangesFreq = 60_000;
  private readonly fullRefreshTransactionsFreq = 3_600_000;

  private readonly notifications: PushNotificationRecord = {
    [TransactionActions.UpsertTransactionSuccess.type]: {
      message: 'Transaction saved',
      isSuccess: true,
    },
    [TransactionActions.UpsertTransactionFail.type]: {
      message: 'Failed to save the transaction',
      isSuccess: false,
    },
    [TransactionActions.DeleteTransactionSuccess.type]: {
      message: 'Transaction deleted',
      isSuccess: true,
    },
    [TransactionActions.DeleteTransactionFail.type]: {
      message: 'Failed to delete the transaction',
      isSuccess: false,
    },
  };

  createTransactionEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.UpsertTransaction),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.transactionService.upsertTransaction(action.input).pipe(
          map((transaction) => TransactionActions.UpsertTransactionSuccess({ transaction })),
          reportEffectError(TransactionActions.UpsertTransactionFail)
        )
      )
    )
  );

  deleteTransactionEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.DeleteTransaction),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.transactionService.deleteTransaction(action.transactionId).pipe(
          map((transaction) => TransactionActions.DeleteTransactionSuccess({ transaction })),
          reportEffectError(TransactionActions.DeleteTransactionFail)
        )
      )
    )
  );

  loadTransactionsEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.LoadTransactions),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        (action.transactionIds?.length
          ? this.transactionService.getByIds(action.transactionIds)
          : this.transactionService.getAll()
        ).pipe(
          map((transactions) => TransactionActions.LoadTransactionsSuccess({ transactions, trigger: action.trigger })),
          reportEffectError(TransactionActions.DeleteTransactionFail)
        )
      )
    )
  );

  notificationEffect$: Observable<Action> = createNotificationEffect(this.actions$, this.notifications);

  publishTransactionModalEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.OpenPublishTransactionModal),
      withLatestFrom(
        this.store.select(ManagerSelectors.list),
        this.store.select(TransactionSelectors.managerIdByTransactionId)
      ),
      switchMap(([action, managers, managerIdByTransactionId]) => {
        return this.dialog
          .open<PublishDealModalComponent, PublishDealOptions, PublishDealModalResult>(PublishDealModalComponent, {
            panelClass: 'publish-deal-modal',
            data: {
              managers,
              managerIdByTransactionId,
              managerId: action.transaction?.managerId,
              transactionId: action.transaction?.transactionId,
            },
          })
          .afterClosed()
          .pipe(
            filter((result) => typeof result === 'object'),
            map((result) =>
              TransactionActions.PublishTransaction({
                docBundleId: result.docBundleId,
                description: action.transaction
                  ? `Transaction: ${action.transaction?.transactionId}`
                  : `Document: ${action.documentId}`,
                clearDatafactoryAndSemtexFiles: result.clearDatafactoryAndSemtexFiles,
                publishToProdusb: result.publishToProdusb,
                publishToProd: result.publishToProd,
                publishToStaging: result.publishToStaging,
                publishToDev: result.publishToDev,
                documentToExtract: result.documentShortName,
                trigger: action.trigger,
              })
            )
          );
      })
    )
  );

  publishTransaction$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.PublishTransaction),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.repoDocBundleService
          .publishRepoDocBundle(action.docBundleId, action.description, {
            clearDatafactoryAndSemtexFiles: action.clearDatafactoryAndSemtexFiles,
            publishToProdusb: action.publishToProdusb,
            publishToDev: action.publishToDev,
            publishToProd: action.publishToProd,
            publishToStaging: action.publishToStaging,
            documentShortName: action.documentToExtract,
          })
          .pipe(
            map(() => TransactionActions.PublishTransactionSuccess({ trigger: action.trigger })),
            reportEffectError(TransactionActions.PublishTransactionFail)
          )
      )
    )
  );

  publishTransactionSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.PublishTransactionSuccess),
      map(() =>
        AppActions.ShowInfoModal({
          data: {
            title: 'Success',
            text: 'Publish in progress - You will receive a slack message when the transaction is published.',
          },
        })
      )
    )
  );

  startPollingTransactionChangesEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.StartPollingTransactionChanges),
      exhaustMap(() => {
        console.debug(
          `[TransactionActions.StartPollingTransactionChanges] Timer started (freq ${this.pollingTransactionChangesFreq} ms)`
        );
        return timer(this.pollingTransactionChangesFreq, this.pollingTransactionChangesFreq);
      }),
      takeUntil(
        this.actions$.pipe(
          ofType(TransactionActions.StopPollingTransactionChanges),
          tap(() => {
            console.debug(`[TransactionActions.StopPollingTransactionChanges] received`);
          })
        )
      ),
      map(() => {
        return TransactionActions.PollingTransactionChanges();
      })
    )
  );

  pollingTransactionChanges$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.PollingTransactionChanges),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.transactionService.getUpdatesSince(store.transaction.lastUpdatedAt).pipe(
          map((transactions) => TransactionActions.PollingTransactionChangesSuccess({ transactions })),
          reportEffectError(TransactionActions.PollingTransactionChangesFail)
        )
      )
    )
  );

  startFullRefreshTransactionsEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.StartPollingTransactionChanges),
      exhaustMap(() => {
        console.debug(
          `[TransactionActions.StartPollingTransactionChanges] Full refresh timer started (freq ${this.fullRefreshTransactionsFreq} ms)`
        );
        return timer(this.fullRefreshTransactionsFreq, this.fullRefreshTransactionsFreq).pipe(
          takeUntil(this.actions$.pipe(ofType(TransactionActions.StopPollingTransactionChanges)))
        );
      }),
      map(() => TransactionActions.FullRefreshTransactions())
    )
  );

  fullRefreshTransactions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.FullRefreshTransactions),
      exhaustMap(() =>
        this.transactionService.getAll().pipe(
          map((transactions) => TransactionActions.FullRefreshTransactionsSuccess({ transactions })),
          reportEffectError(TransactionActions.FullRefreshTransactionsFail)
        )
      )
    )
  );
}
